import { MutableRefObject, useEffect, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { VIEW_ACTION } from './ToggleViewButton';
import CheckboxController from '../checkbox/CheckboxController';
import { ConfirmModalProps } from '../confirmModal';
import { useTranslation } from 'react-i18next';
import AllColumnSetting from '../columns/AllColumnSetting';
import ModeViewTable, {
  ModeViewTableState,
} from '../ModeViewTable/ModeViewTable';

const useViewModel = ({
  errorCount,
  validating,
  disableExportHasMoreDataRef,
  changeModeView,
  checkboxController,
  onRemove,
  onDuplicate,
  showConfirmModal,
  allColumnSetting,
  modeViewTable,
}: {
  errorCount: MutableRefObject<number>;
  disableExportHasMoreDataRef: MutableRefObject<boolean>;
  validating: boolean;
  changeModeView: (showOnlyError: boolean) => void;
  checkboxController?: CheckboxController;
  onRemove: (rows: number[]) => void;
  onDuplicate: (rows: number[]) => void;
  showConfirmModal: (props: ConfirmModalProps) => void;
  allColumnSetting: AllColumnSetting;
  modeViewTable: ModeViewTable;
}) => {
  const [errorCountNumber, setErrorCountNumber] = useState(errorCount.current);
  const [isShowOnlyError, setIsShowOnlyError] = useState(false);
  const [disableExportHasMoreData, setDisableExportHasMoreData] =
    useState(false);
  const [isExportFilePopoverShow, setIsExportFilePopoverShow] = useState(false);
  const isExportDisabled = validating;
  const isErrorCount = errorCountNumber === 0;
  const [selectedCount, setSelectedCount] = useState(0);
  const { t } = useTranslation();
  const [isLoadingDuplicate, setIsLoadingDuplicate] = useState(false);
  const [filterCount, setFilterCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setErrorCountNumber(errorCount.current);
      setDisableExportHasMoreData(disableExportHasMoreDataRef.current);
    }, 150);

    return () => {
      clearTimeout(interval);
    };
  }, [errorCount, disableExportHasMoreDataRef]);

  useEffect(() => {
    const subscription = allColumnSetting
      .getFilterStrategy()
      .filterObservable()
      .subscribe(() => {
        setFilterCount(allColumnSetting.filterCount());
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [allColumnSetting]);

  useEffect(() => {
    const subscription = modeViewTable
      .modeViewTableObservable()
      .subscribe(() => {
        setIsShowOnlyError(
          modeViewTable.getViewState() === ModeViewTableState.ERROR
        );
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [modeViewTable]);

  useEffect(() => {
    if (checkboxController) {
      const subscription = checkboxController
        .checkBoxObservable()
        .subscribe(() => {
          const checkedCount = checkboxController.getCheckedCount();
          setSelectedCount(checkedCount);
        });

      return () => {
        subscription.unsubscribe();
      };
    } else {
      return () => {};
    }
  }, [checkboxController]);

  const getOnExportFileHoverProps = () => {
    if (!isMobile && !isTablet) {
      return {
        onMouseEnter: () => setIsExportFilePopoverShow(true),
        onMouseLeave: () => setIsExportFilePopoverShow(false),
      };
    } else {
      return {
        onClick: () => setIsExportFilePopoverShow((isShow) => !isShow),
        onMouseLeave: () => setIsExportFilePopoverShow(false),
      };
    }
  };

  const onToggleViewChange = (action: VIEW_ACTION) => {
    if (action === VIEW_ACTION.SHOW_ALL) {
      changeModeView(false);
      setIsShowOnlyError(false);
    } else {
      changeModeView(true);
      setIsShowOnlyError(true);
    }
    checkboxController?.setCheckAll(false);
  };

  const onDeleteClick = () => {
    if (checkboxController) {
      const removeRows = checkboxController
        .getCheckedState()
        .getCheckedRows(true);
      if (removeRows.length > 1) {
        showConfirmModal({
          title: t('txt_confirm_title'),
          description: t('txt_confirm_delete_rows', {
            selectedRow: removeRows.length,
          }),
          onClickNegativeButton: () => {
            return new Promise<void>((resolve) => {
              setTimeout(() => {
                onRemove(removeRows);
                resolve();
              }, 100);
            });
          },
          onClickPositiveButton: () => {},
          isShowIcon: true,
          isShowCloseIcon: false,
          disabledClickOutside: true,
          textNegativeButton: t('txt_delete_all'),
          textPositiveButton: t('txt_cancel'),
        });
      } else {
        onRemove(removeRows);
      }
    }
  };

  const onDuplicateClick = () => {
    setIsLoadingDuplicate(true);
    if (checkboxController) {
      setTimeout(() => {
        onDuplicate(checkboxController.getCheckedState().getCheckedRows(true));
        setIsLoadingDuplicate(false);
      }, 0);
    }
  };

  return {
    isExportDisabled,
    isErrorCount,
    errorCountNumber,
    setIsShowOnlyError,
    isShowOnlyError,
    disableExportHasMoreData,
    isExportFilePopoverShow,
    getOnExportFileHoverProps,
    onToggleViewChange,
    selectedCount,
    onDeleteClick,
    onDuplicateClick,
    isLoadingDuplicate,
    filterCount,
  };
};

export default useViewModel;
